import { FC, useEffect, useState, useRef, useCallback } from "react";
import { PurchaseOrderState } from "../types/api";

import {
  PurchaseOrder,
  PurchaseOrderLineItem,
  PurchaseOrderLineItemReceipt,
  ProductVariant,
} from "../types/api";
import { ManagedLineItem, LineItemStatus } from "../types/lineItemTypes";
import { DataGrid, GridColDef, GridRowModel, GridRowSelectionModel } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { currencyFormatter } from "../util";
import DeleteIcon from "@mui/icons-material/Delete";
import { MISSING_PRODUCT_IMAGE_URL } from "../config";
import ReceivedLineItemTable from "./received-lineitem-table.component";
import { ViewVariantStockModal } from "./modal.view-variantstock";
import { dataTables } from "../styles";

import _ from "lodash";

interface Props {
  items: ManagedLineItem[];
  parentPo: PurchaseOrder;
  onUpdateQuantity?: (tempId: string, quantity: number) => void;
  onUpdateField?: (tempId: string, field: string, value: any) => void;
  onDeleteRow?: (tempId: string) => void;
  onRowsToReceiveChange?: Function;
  onDeleteReceipts?: (receipts: PurchaseOrderLineItemReceipt[]) => void;
  onCreateBill?: (receipts: PurchaseOrderLineItemReceipt[]) => void;
  onMoveItemUp?: (tempId: string) => void;
  onMoveItemDown?: (tempId: string) => void;
  allowEditQuantity?: boolean;
  hideReceiveCols?: boolean;
  allowReordering?: boolean;
  highlightedTempId?: string;
  onClearHighlight?: () => void;
}

/**
 * True when a purchase order line refers to a product that no longer exists
 * in AMS: it has no variant link and is not a special order (SPE SKUs never
 * have one).
 */
export const isDeletedProductLine = (poli: { variantGuid?: string | null; variantSku?: string }): boolean =>
  !poli.variantGuid && !(poli.variantSku || "").startsWith("SPE");

const PurchaseOrderLineItemTable: FC<Props> = (props: Props) => {
  const { items, parentPo } = props;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  // Save scroll position before updates
  const saveScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      const scrollElement = scrollContainerRef.current.querySelector('.MuiDataGrid-virtualScroller');
      if (scrollElement) {
        setSavedScrollPosition(scrollElement.scrollTop);
      }
    }
  }, []);

  // Restore scroll position after updates
  const restoreScrollPosition = useCallback(() => {
    if (scrollContainerRef.current && savedScrollPosition > 0) {
      setTimeout(() => {
        const scrollElement = scrollContainerRef.current?.querySelector('.MuiDataGrid-virtualScroller');
        if (scrollElement) {
          scrollElement.scrollTop = savedScrollPosition;
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [savedScrollPosition]);

  const updateQuantity = (e: any) => {
    const tempId = e.target.id.substring("quantity-".length);
    const newQuantity = +e.target.value > 0 ? +e.target.value : 1;

    if (props.onUpdateQuantity) {
      try {
        props.onUpdateQuantity(tempId, newQuantity);
      } catch (error) {
        // Validation error - could show user notification here
        console.error('Error updating quantity:', error.message);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: "id",
    },
    { field: "guid" },
    {
      field: "image",
      headerName: "Image",
      sortable: false,
      minWidth: 80,
      flex: 1,
      align: "center",
      renderCell: (params) => (
        <img
          src={params.value[0]}
          height={40}
          alt={params.value[1]}
          onClick={() => window.open(params.value[0])}
          style={{ cursor: "pointer" }}
        />
      ),
    },
    // { field: "sku", headerName: "SKU", width: 140 },
    {
      field: "details",
      headerName: "Item Name",
      minWidth: 350,
      flex: 2,
      renderCell: (params) => {
        // Info-only "on sale" flag, derived from the variant's live prices
        // (previous price higher than current retail). Not persisted with the
        // order -- it just flags items likely sold on sale so they can be
        // considered for skipping on restock.
        const variant: ProductVariant | undefined = params.value.variant;
        const onSale =
          !!variant &&
          variant.previousPrice != null &&
          variant.previousPrice > variant.retailPrice;
        // A line whose product has been deleted keeps its SKU but loses its
        // variant link (the FK is set null on delete). Special orders never
        // had one, so an unlinked non-SPE line means the product is gone.
        const deleted = isDeletedProductLine(params.value.poli);
        const nameText = params.value.poli.product
          ? params.value.poli.product.name
          : params.value.poli?.description;
        const captionText = params.value.poli.product
          ? `${params.value.poli.product.productType} - ${params.value.variant.sku}`
          : deleted
          ? `Deleted product - ${params.value.poli.variantSku}`
          : `Special Order - ${params.value.poli.variantSku}`;
        return (
          <Box
            sx={{
              textAlign: "left",
              display: "flex",
              flexDirection: "column",
              gap: 0,
              minWidth: 0,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              {/* Name shrinks and ellipsises; the pill is flex-shrink:0 so it is
                  never clipped, whatever the name length. Full name on hover. */}
              <Typography
                variant="body2"
                noWrap
                title={nameText}
                sx={{
                  fontWeight: "medium",
                  minWidth: 0,
                  flex: "0 1 auto",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {nameText}
              </Typography>
              {onSale && (
                <Chip label="sale" color="error" size="small" sx={{ flexShrink: 0 }} />
              )}
              {deleted && (
                <Chip
                  label="deleted"
                  color="warning"
                  size="small"
                  sx={{ flexShrink: 0 }}
                  title="This product has been deleted from AMS. Remove the line, or recreate the product with the same SKU to relink it."
                  data-testid="deleted-product-pill"
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap>
              {captionText}
            </Typography>
          </Box>
        );
      },
    },
    { field: "packSize", headerName: "Pk. Size", flex: 1, align: "right" },
    {
      field: "barcode",
      headerName: "Barcode",
      flex: 1,
      editable: props.allowEditQuantity,
      minWidth: 140,
    },
    {
      field: "supplierCode",
      headerName: "Supplier Code",
      minWidth: 140,
      flex: 1,
      editable: props.allowEditQuantity,
    },
    {
      field: "quantityOrdered",
      headerName: "Qty.",
      minWidth: 70,
      align: "right",
      flex: 1,
      renderCell: (params) =>
        props.allowEditQuantity ? (
          <TextField
            id={"quantity-" + params.row.tempId}
            inputProps={{
              style: { textAlign: "right" },
              min: 1,
            }}
            type="number"
            value={params.value}
            onChange={(e) => updateQuantity(e)}
          />
        ) : (
          params.value
        ),
    },
    {
      field: "taxRate",
      headerName: "Tax",
      minWidth: 40,
      flex: 1,
      align: "right",
    },
    {
      field: "stockOnHand",
      headerName: "Current Stock Level",
      minWidth: 40,
      flex: 1,
      align: "right",
    },
  ];

  // Add reordering column if allowed
  if (props.allowReordering && props.onMoveItemUp && props.onMoveItemDown) {
    columns.push({
      field: "reorder",
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      editable: false,
      disableReorder: true,
      minWidth: 50,
      width: 50,
      headerName: "Order",
      align: "center",
      renderCell: (params) => {
        // Find the current row's position in the sorted list
        const sortedRows = [...rows].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        const currentIndex = sortedRows.findIndex(row => row.id === params.row.id);
        const isFirst = currentIndex === 0;
        const isLast = currentIndex === sortedRows.length - 1;

        return (
          <Box sx={{
            display: "flex",
            gap: 0.5,
            justifyContent: "center",
            alignItems: "center",
            height: "100%"
          }}>
            {!isFirst && (
              <Typography
                component="span"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (props.onMoveItemUp) {
                    props.onMoveItemUp(params.row.tempId);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  fontSize: "16px",
                  lineHeight: 1,
                  opacity: 1,
                  "&:hover": { color: "primary.main" }
                }}
              >
                ↑
              </Typography>
            )}
            {!isLast && (
              <Typography
                component="span"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (props.onMoveItemDown) {
                    props.onMoveItemDown(params.row.tempId);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  fontSize: "16px",
                  lineHeight: 1,
                  opacity: 1,
                  "&:hover": { color: "primary.main" }
                }}
              >
                ↓
              </Typography>
            )}
          </Box>
        );
      },
    });
  }

  if (props.allowEditQuantity && props.onDeleteRow) {
    columns.push({
      field: "delete",
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      minWidth: 70,
      flex: 1,
      headerName: "Delete",
      renderCell: (params) => (
        <IconButton
          value={"delete-" + params.row.sku}
          onClick={() => props.onDeleteRow && props.onDeleteRow(params.row.tempId)}
        >
          <DeleteIcon />
        </IconButton>
      ),
    });
  }

  if (props.hideReceiveCols !== true) {
    columns.push({
      field: "quantityToReceive",
      type: "number",
      headerName: "To Receive",
      editable: true,
      flex: 1,
      align: "right",
    });
    columns.push({
      field: "receivePrice",
      headerName: "Receive @ £",
      width: 100,
      editable: true,
      flex: 1,
      align: "right",
    });
    columns.push({
      field: "retailPrice",
      headerName: "Retail £",
      width: 80,
      flex: 1,
      editable: true,
      align: "right",
    });
  }

  columns.push({
    field: "view",
    headerName: "View",
    disableColumnMenu: true,
    sortable: false,
    filterable: false,
    minWidth: 60,
    flex: 1,
    align: "center",
    renderCell: (params) => (
      <ViewVariantStockModal color="inherit" variant={params.row} />
    ),
  });

  // Build the main table of unreceived, not deleted things
  const [rows, setRows] = useState([]);
  const [editedValues, setEditedValues] = useState(new Map()); // Track edited values
  const previousReceiptCounts = useRef(new Map<string, number>()); // Track receipt counts to detect when receives occur

  // Clear edited state for items that have been received
  const clearEditStateForItems = useCallback((tempIds: string[]) => {
    setEditedValues(prev => {
      const updated = new Map(prev);
      tempIds.forEach(id => updated.delete(id));
      return updated;
    });

    // Also deselect these rows from the selection model
    setRowSelectionModel(prev => {
      if (!prev) return prev;

      if (prev.type === 'exclude') {
        // Add to exclusion list
        const newExcluded = new Set(prev.ids);
        tempIds.forEach(id => newExcluded.add(id));
        return { ...prev, ids: newExcluded };
      } else {
        // Remove from inclusion list
        const newIncluded = new Set(prev.ids);
        tempIds.forEach(id => newIncluded.delete(id));
        return { ...prev, ids: newIncluded };
      }
    });
  }, []);

  useEffect(() => {
    saveScrollPosition(); // Save current position before updating

    if (!_.isEmpty(items)) {
      // DETECTION: Check if any items have new receipts since last render
      const currentReceiptCounts = new Map<string, number>();
      const itemsWithNewReceipts: string[] = [];

      items.forEach(poli => {
        const tempId = poli.tempId;
        const currentCount = poli.receipts?.length || 0;
        currentReceiptCounts.set(tempId, currentCount);

        const previousCount = previousReceiptCounts.current.get(tempId) || 0;
        if (currentCount > previousCount) {
          itemsWithNewReceipts.push(tempId);
        }
      });

      // Clear edited state for items that have been received
      if (itemsWithNewReceipts.length > 0) {
        clearEditStateForItems(itemsWithNewReceipts);
      }

      // Update the ref for next comparison
      previousReceiptCounts.current = currentReceiptCounts;

      const activeItems = items.filter((poli) => poli.status === LineItemStatus.ACTIVE);
      const sortedItems = activeItems.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      setRows(
        sortedItems.map((poli: ManagedLineItem) => {
            // Calculate and store the quantity left to receive
            const qtr =
              poli.quantityOrdered -
              poli.receipts.map((r) => r.received).reduce((a, b) => a + b, 0);

            return { ...poli, qtr: qtr };
          })
          .filter((poli) => poli.qtr > 0)
          .map((poli: ManagedLineItem) => {
            if (poli.product) {
              const variant: ProductVariant = poli.product.variants.filter(
                (v) => v.guid === poli.variantGuid
              )[0];

              // Check if this row has been edited
              const editedRow = editedValues.get(poli.tempId);

              const row = {
                id: poli.tempId, // Use tempId instead of problematic ID system
                tempId: poli.tempId,
                sequence: poli.sequence,
                guid: variant.guid,
                sku: variant.sku,
                barcode: poli.variantUPC,
                image: [poli.variantMediaURI || "", poli.name],
                details: { poli, variant },
                supplierCode: poli.variantSupplierCode,
                packSize: poli.packSize || 1,
                quantityOrdered: poli.quantityOrdered,
                taxRate: `${poli.variantTaxRate}%`,
                stockOnHand: poli.stockOnHand,
                quantityToReceive: editedRow?.quantityToReceive ?? poli.qtr,
                receivePrice: editedRow?.receivePrice ?? currencyFormatter.format(poli.itemCost / 100),
                retailPrice: editedRow?.retailPrice ?? currencyFormatter.format(
                  (poli.retailPriceTmp || variant?.retailPrice || 0) / 100
                ),
                receive: poli,
                view: poli,
              };

              return row;
            }

            // This product is not in our inventory service - maybe a new one, or an SPE.
            const editedRow = editedValues.get(poli.tempId);
            
            return {
              id: poli.tempId,
              tempId: poli.tempId,
              sequence: poli.sequence,
              guid: poli.guid,
              sku: poli.variantSku,
              barcode: poli.variantUPC,
              image: [MISSING_PRODUCT_IMAGE_URL, poli.name],
              details: { poli },
              supplierCode: poli.variantSupplierCode,
              packSize: 1,
              quantityOrdered: poli.quantityOrdered,
              taxRate: `${poli.variantTaxRate}%`,
              stockOnHand: "N/A",
              quantityToReceive: editedRow?.quantityToReceive ?? poli.qtr,
              receivePrice: editedRow?.receivePrice ?? currencyFormatter.format(poli.itemCost / 100),
              retailPrice: editedRow?.retailPrice ?? "N/A",
            };
          })
      );

      // Restore scroll position after state update
      restoreScrollPosition();
    }
  }, [items, saveScrollPosition, restoreScrollPosition, editedValues, clearEditStateForItems]);

  // Build a map of received items by date for later rendering
  const [receiptsByDate, setReceiptsByDate] =
    useState<Map<Date, PurchaseOrderLineItemReceipt[]>>();

  useEffect(() => {
    const r = new Map<Date, PurchaseOrderLineItemReceipt[]>();

    if (!_.isEmpty(items)) {
      items
        .filter((poli) => !_.isEmpty(poli.receipts))
        .forEach((poli) => {
          poli.receipts.forEach((receipt) => {
            let receipts = r.get(receipt.date);

            if (!receipts) {
              receipts = [];
            }

            receipt["poLineItem"] = poli;
            receipts.push(receipt);
            r.set(receipt.date, receipts);
          });
        });

      setReceiptsByDate(r);
    }
  }, [parentPo]);

  // Set up selection checkboxes for receiving line items
  const enableSelection =
    props.onRowsToReceiveChange && parentPo.state === PurchaseOrderState.ACTIVE
      ? true
      : false;
      

  // Setup a selection model for the receive checkboxes - an array of IDs which are selected
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  useEffect(() => {
    if (props.onRowsToReceiveChange && rowSelectionModel) {
      let selectedRows: any[] = [];
      
      if (rowSelectionModel.type === 'exclude') {
        // "exclude" type means select all rows EXCEPT those in the ids Set
        const excludedIds = rowSelectionModel.ids || new Set();
        selectedRows = rows.filter((r) => !excludedIds.has(r.id));
      } else {
        // "include" type (or default) means select only those in the ids Set
        const includedIds = rowSelectionModel.ids || new Set();
        selectedRows = rows.filter((r) => includedIds.has(r.id));
      }
      
      props.onRowsToReceiveChange(selectedRows);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel]);

  // When highlightedTempId changes, push the flag into the rows so the DataGrid
  // re-renders the affected row (rows is useState, so getRowClassName alone won't
  // fire when only the prop changes — the row data must change too).
  useEffect(() => {
    setRows(prev => prev.map(row => ({
      ...row,
      isHighlighted: row.tempId === props.highlightedTempId,
    })));
  }, [props.highlightedTempId]);

  const getRowClassName = (params: any) =>
    params.row.isHighlighted ? "duplicate-highlight" : "";

  // When the checkbox selection changes update the list of rows to receive in the parent, for when the receipt button is pressed.
  const _selectionChange = (model: GridRowSelectionModel) => {
    if (enableSelection) {
        setRowSelectionModel(model);
    }
  };

  // Called when exiting an edit state
  const _handleEdits = (
    newRow: GridRowModel
  ): GridRowModel => {
    // Validate and constrain the quantity to receive
    if (newRow.quantityToReceive > newRow.quantityOrdered)
      newRow.quantityToReceive = newRow.quantityOrdered;
    if (newRow.quantityToReceive < 1) newRow.quantityToReceive = 1;

    // Format prices
    const formattedReceivePrice = `£${Number(
      newRow.receivePrice.replace(/[^0-9.]/g, "")
    ).toFixed(2)}`;
    const formattedRetailPrice = `£${Number(
      newRow.retailPrice.replace(/[^0-9.]/g, "")
    ).toFixed(2)}`;

    // Store edited values to persist across re-renders
    setEditedValues(prev => {
      const updated = new Map(prev);
      updated.set(newRow.tempId, {
        quantityToReceive: newRow.quantityToReceive,
        receivePrice: formattedReceivePrice,
        retailPrice: formattedRetailPrice
      });
      return updated;
    });

    // Find and update the row in the local state (this is expected by DataGrid)
    const r = rows.find((i) => i.id === newRow.id);
    if (r) {
      r.quantityToReceive = newRow.quantityToReceive;
      r.receivePrice = formattedReceivePrice;
      r.retailPrice = formattedRetailPrice;
    }

    // Format the return row with proper prices
    newRow.receivePrice = formattedReceivePrice;
    newRow.retailPrice = formattedRetailPrice;

    // If we are receiving we need to update the RowsToReceive model
    if (props.onRowsToReceiveChange) {
      // Initialize or update the selection model to include the edited row
      let selectedIds: Set<any>;
      
      if (!rowSelectionModel) {
        // Initialize with a new selection model containing just this row
        setRowSelectionModel({ type: 'include', ids: new Set([newRow.id]) } as GridRowSelectionModel);
        selectedIds = new Set([newRow.id]);
      } else {
        // Handle both include and exclude selection models
        if (rowSelectionModel.type === 'exclude') {
          // In exclude mode, we need to remove this row from the excluded set
          const excludedIds = new Set(rowSelectionModel.ids || []);
          excludedIds.delete(newRow.id); // Remove from excluded = include this row
          setRowSelectionModel({ type: 'exclude', ids: excludedIds } as GridRowSelectionModel);
          selectedIds = rows.filter((r) => !excludedIds.has(r.id)).reduce((set, row) => set.add(row.id), new Set());
        } else {
          // In include mode, add this row to the included set
          selectedIds = new Set(rowSelectionModel.ids || []);
          if (!selectedIds.has(newRow.id)) {
            const updatedIds = new Set(selectedIds);
            updatedIds.add(newRow.id);
            setRowSelectionModel({ type: 'include', ids: updatedIds } as GridRowSelectionModel);
            selectedIds = updatedIds;
          }
        }
      }

      // Pass the updated rows to the parent with the current selection model
      props.onRowsToReceiveChange(
        rows.filter((r) => selectedIds.has(r.id))
      );
    } else {
      // We are not receiving, so we must be changing some other details
      if (props.onUpdateField) {
        props.onUpdateField(newRow.tempId, 'variantUPC', newRow.barcode);
        props.onUpdateField(newRow.tempId, 'variantSupplierCode', newRow.supplierCode);
      }
    }

    // Finally commit the changes to the current view
    return newRow;
  };

  // The first box is all the unreceived items, and is hidden if there are none. This is followed
  // by any number of ReceivedLineItemTable components
  return (
    <>
      {_.isEmpty(items) && (
        <Box sx={{ m: 1 }}>
          <p>
            Please create some line items by selecting either Add Product or Add
            Special Order
          </p>
        </Box>
      )}
      {!_.isEmpty(rows) && (
        <Box sx={{ m: 2 }} ref={scrollContainerRef}>
          <Typography variant="body1" align="left" sx={{ fontWeight: "bold" }}>
            {rows.length} row{rows.length > 1 ? "s" : ""} {parentPo.state !== PurchaseOrderState.DRAFT ? " not yet received..." : ""}
          </Typography>
          <DataGrid
            checkboxSelection={enableSelection}
            rowSelectionModel={enableSelection ? rowSelectionModel : undefined}
            onRowSelectionModelChange={enableSelection ? _selectionChange : undefined}
            columnVisibilityModel={{
              id: false,
              guid: false
            }}
            rows={rows}
            columns={columns} 
            getRowId={(row) => row.id}
            autoHeight
            density="compact"
            rowHeight={60}
            editMode="cell"
            processRowUpdate={_handleEdits}
            onProcessRowUpdateError={(e) => alert(e)}
            disableRowSelectionOnClick
            getRowClassName={getRowClassName}
            onRowClick={(params) => {
              if (params.row.tempId === props.highlightedTempId) {
                props.onClearHighlight?.();
              }
            }}
            sx={{
              ...dataTables,
              "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row.duplicate-highlight": {
                backgroundColor: "warning.light",
                "&:hover": { backgroundColor: "warning.light" },
              },
            }}
          />
        </Box>
      )}
      {!_.isEmpty(receiptsByDate) && <Divider />}
      {!_.isEmpty(receiptsByDate) &&
        Array.from(receiptsByDate.entries())
          .sort()
          .reverse()
          .map((entry) => (
            <Box>
              <ReceivedLineItemTable
                onDeleteReceipts={() => {
                  props.onDeleteReceipts(entry[1]);
                }}
                onCreateBill={
                  props.onCreateBill
                    ? () => props.onCreateBill(entry[1])
                    : undefined
                }
                receipts={entry[1]}
                date={new Date(entry[0])}
              />
            </Box>
          ))}
    </>
  );
};

export default PurchaseOrderLineItemTable;
